import React, { useState, useMemo } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
} from 'react-simple-maps';

const CustomerGrowth = ({ data }) => {
  const [selectedRegion, setSelectedRegion] = useState('Asian Country');
  const [zoom, setZoom] = useState(1);
  const [center, setCenter] = useState([0, 20]);

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev * 1.5, 4));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev / 1.5, 0.5));
  };

  const defaultCountryData = useMemo(
    () => [
      {
        name: 'Saudi Arabia',
        percentage: 42,
        color: 'bg-green-600',
        coordinates: [45.0792, 23.8859],
        fillColor: '#059669',
        countryCode: 'SAU',
        alternativeCodes: ['SA', 'Saudi Arabia'],
      },
      {
        name: 'India',
        percentage: 28,
        color: 'bg-orange-500',
        coordinates: [78.9629, 20.5937],
        fillColor: '#F97316',
        countryCode: 'IND',
        alternativeCodes: ['IN', 'India'],
      },
      {
        name: 'South Korea',
        percentage: 17,
        color: 'bg-blue-500',
        coordinates: [127.7669, 35.9078],
        fillColor: '#3B82F6',
        countryCode: 'KOR',
        alternativeCodes: ['KR', 'South Korea', 'Republic of Korea'],
      },
      {
        name: 'Indonesia',
        percentage: 13,
        color: 'bg-red-500',
        coordinates: [113.9213, -0.7893],
        fillColor: '#EF4444',
        countryCode: 'IDN',
        alternativeCodes: ['ID', 'Indonesia'],
      },
    ],
    []
  );

  // For map highlighting logic below, keep a stable reference list
  const countryData = defaultCountryData;

  const geoUrl =
    'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

  const dynamicList = React.useMemo(() => {
    if (data && typeof data === 'object') {
      const key =
        selectedRegion in data ? selectedRegion : Object.keys(data)[0];
      const arr = Array.isArray(data[key]) ? data[key] : [];
      if (arr.length) {
        return arr.map((c) => ({
          name: c.name || 'Unknown',
          percentage: c.value || 0,
          color: c.color ? '' : 'bg-green-600',
        }));
      }
    }
    return defaultCountryData;
  }, [data, selectedRegion, defaultCountryData]);

  return (
    <div
      className="bg-white rounded-[12px] p-4 sm:p-6"
      style={{ boxShadow: '0px 4px 16px 0px #0000001A' }}
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-[#373737] poppins text-[18px] sm:text-[20px] lg:text-[24px] leading-[27px] sm:leading-[30px] lg:leading-[36px] tracking-[-0.96px] font-bold mb-1">
            Customer Growth
          </h2>
          <p className="text-[#9CA3AF] poppins text-[12px] sm:text-[14px] leading-[18px] sm:leading-[21px] tracking-[-0.56px]">
            Of the week based on country
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="relative">
            <select
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
              className="appearance-none bg-white border border-[#E5E7EB] rounded-[8px] px-3 sm:px-4 py-2 pr-6 sm:pr-8 text-[#374151] poppins text-[12px] sm:text-[14px] leading-[18px] sm:leading-[21px] tracking-[-0.56px] cursor-pointer hover:border-[#D1D5DB] transition-colors"
            >
              <option value="Asian Country">Asian Country</option>
              <option value="European Country">European Country</option>
              <option value="American Country">American Country</option>
            </select>
            <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 4.5L6 7.5L9 4.5"
                  stroke="#6B7280"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
          <button className="p-2 hover:bg-[#F3F4F6] rounded-[4px] transition-colors">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="8" cy="3" r="1.5" fill="#9CA3AF" />
              <circle cx="8" cy="8" r="1.5" fill="#9CA3AF" />
              <circle cx="8" cy="13" r="1.5" fill="#9CA3AF" />
            </svg>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col lg:flex-row items-start gap-4 lg:gap-6">
        {/* World Map */}
        <div className="flex-1 relative w-full">
          <div className="bg-[#F9FAFB] rounded-[8px] p-2 sm:p-4 h-[200px] sm:h-[250px] lg:h-[280px] relative overflow-hidden">
            {/* World Map */}
            <ComposableMap
              projection="geoMercator"
              projectionConfig={{
                scale: 100,
                center: center,
              }}
              width={800}
              height={400}
              className="w-full h-full"
            >
              <ZoomableGroup zoom={zoom} center={center} onMove={setCenter}>
                <Geographies geography={geoUrl}>
                  {({ geographies }) =>
                    geographies.map((geo) => {
                      // Check if this geography matches any of our highlighted countries
                      const name = geo.properties.NAME;
                      const isoA3 = geo.properties.ISO_A3;
                      const isoA2 = geo.properties.ISO_A2;

                      // Debug: Log all countries to see what we have
                      console.log(`${name}: ISO_A3=${isoA3}, ISO_A2=${isoA2}`);

                      // More specific matching for our target countries
                      let highlightedCountry = null;

                      if (
                        name === 'Saudi Arabia' ||
                        isoA3 === 'SAU' ||
                        isoA2 === 'SA'
                      ) {
                        highlightedCountry = countryData.find(
                          (c) => c.name === 'Saudi Arabia'
                        );
                      } else if (
                        name === 'India' ||
                        isoA3 === 'IND' ||
                        isoA2 === 'IN'
                      ) {
                        highlightedCountry = countryData.find(
                          (c) => c.name === 'India'
                        );
                      } else if (
                        name === 'South Korea' ||
                        name === 'Republic of Korea' ||
                        isoA3 === 'KOR' ||
                        isoA2 === 'KR'
                      ) {
                        highlightedCountry = countryData.find(
                          (c) => c.name === 'South Korea'
                        );
                      } else if (
                        name === 'Indonesia' ||
                        isoA3 === 'IDN' ||
                        isoA2 === 'ID'
                      ) {
                        highlightedCountry = countryData.find(
                          (c) => c.name === 'Indonesia'
                        );
                      }

                      // Debug log when we find a highlighted country
                      if (highlightedCountry) {
                        console.log(
                          `Coloring ${name} with ${highlightedCountry.fillColor}`
                        );
                      }

                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          fill={
                            highlightedCountry
                              ? highlightedCountry.fillColor
                              : '#E5E7EB'
                          }
                          stroke={highlightedCountry ? '#FFFFFF' : '#D1D5DB'}
                          strokeWidth={highlightedCountry ? 1 : 0.5}
                          style={{
                            default: {
                              fill: highlightedCountry
                                ? highlightedCountry.fillColor
                                : '#E5E7EB',
                              stroke: highlightedCountry
                                ? '#FFFFFF'
                                : '#D1D5DB',
                              strokeWidth: highlightedCountry ? 1 : 0.5,
                              outline: 'none',
                            },
                            hover: {
                              fill: highlightedCountry
                                ? highlightedCountry.fillColor
                                : '#F3F4F6',
                              stroke: highlightedCountry
                                ? '#FFFFFF'
                                : '#9CA3AF',
                              strokeWidth: highlightedCountry ? 1.5 : 1,
                              outline: 'none',
                            },
                            pressed: {
                              fill: highlightedCountry
                                ? highlightedCountry.fillColor
                                : '#E5E7EB',
                              stroke: highlightedCountry
                                ? '#FFFFFF'
                                : '#9CA3AF',
                              strokeWidth: highlightedCountry ? 1.5 : 1,
                              outline: 'none',
                            },
                          }}
                        />
                      );
                    })
                  }
                </Geographies>
              </ZoomableGroup>
            </ComposableMap>

            {/* Zoom Controls */}
            <div className="absolute bottom-4 left-4 flex flex-col gap-1">
              <button
                onClick={handleZoomIn}
                className="w-8 h-8 bg-white border border-[#E5E7EB] rounded-[4px] flex items-center justify-center hover:bg-[#F9FAFB] transition-colors"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 2V10M2 6H10"
                    stroke="#6B7280"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
              <button
                onClick={handleZoomOut}
                className="w-8 h-8 bg-white border border-[#E5E7EB] rounded-[4px] flex items-center justify-center hover:bg-[#F9FAFB] transition-colors"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2 6H10"
                    stroke="#6B7280"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Country Growth List */}
        <div className="w-full lg:w-[200px] space-y-3 sm:space-y-4">
          {dynamicList.map((country, index) => (
            <div key={country.name} className="flex items-center gap-3">
              <div className="w-4 h-4 bg-[#F3F4F6] rounded-[2px] flex items-center justify-center">
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="1"
                    y="1"
                    width="8"
                    height="8"
                    rx="1"
                    stroke="#9CA3AF"
                    strokeWidth="0.5"
                  />
                  <rect
                    x="2.5"
                    y="2.5"
                    width="5"
                    height="5"
                    rx="0.5"
                    fill="#9CA3AF"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[#374151] poppins text-[12px] leading-[18px] tracking-[-0.48px] font-medium">
                    {country.name}
                  </span>
                  <span className="text-[#6B7280] poppins text-[12px] leading-[18px] tracking-[-0.48px]">
                    {country.percentage}%
                  </span>
                </div>
                <div className="w-full bg-[#F3F4F6] rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${country.color}`}
                    style={{ width: `${country.percentage}%` }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CustomerGrowth;
